/**
 * useAllMcpServices - Hook for fetching ALL available MCP services
 * 
 * Blue Steel Excellence: Unified tool selection across ALL servers
 * - Fetches all physical MCP services from Membership
 * - Caches results for 5 minutes
 * - Returns service list with tool counts
 * 
 * @example
 * ```tsx
 * const { services, loading, error } = useAllMcpServices();
 * ```
 */

import { useState, useEffect, useCallback } from 'react';

export interface MCPService {
  id: string;
  name: string;
  display_name: string;
  description?: string;
  service_type: string;
  api_endpoint?: string;
  provider?: string;
  tool_count?: number;
  resource_count?: number;
  prompt_count?: number;
  is_backend?: boolean;
  icon?: string;
  status?: 'active' | 'inactive' | 'error';
}

interface UseAllMcpServicesResult {
  services: MCPService[];
  loading: boolean;
  error: Error | null;
  refresh: () => void;
}

// In-memory cache with 5-minute TTL
let servicesCache: { data: MCPService[]; timestamp: number } | null = null;
const CACHE_TTL_MS = 5 * 60 * 1000;

export function useAllMcpServices(): UseAllMcpServicesResult {
  const [services, setServices] = useState<MCPService[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const refresh = useCallback(() => {
    servicesCache = null;
    setRefreshKey(prev => prev + 1);
  }, []);

  useEffect(() => {
    let mounted = true;

    const fetchServices = async () => {
      // Check cache first
      if (servicesCache && Date.now() - servicesCache.timestamp < CACHE_TTL_MS) {
        setServices(servicesCache.data);
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        // Fetch all MCP services from Membership API
        const response = await fetch('/api/v1/mcp/services', {
          credentials: 'include',
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch MCP services: ${response.statusText}`);
        }

        const data = await response.json();
        // API returns { services: [...], total_count: N }
        const servicesList: MCPService[] = data.services || data || [];

        // Update cache
        servicesCache = { data: servicesList, timestamp: Date.now() };

        if (mounted) {
          setServices(servicesList);
          setLoading(false);
        }
      } catch (err) {
        console.error('[useAllMcpServices] Error:', err);
        if (mounted) {
          setError(err instanceof Error ? err : new Error(String(err)));
          setLoading(false);
        }
      }
    };

    fetchServices();

    return () => {
      mounted = false;
    };
  }, [refreshKey]);

  return { services, loading, error, refresh };
}

/**
 * Clear the services cache
 */
export function clearServicesCache(): void {
  servicesCache = null;
}

